"use client"

import { useState } from "react"

import { cn } from "@/common/lib/utils"
import { MARK_RADIUS, SEQUENTIAL_VAR, STATUS_VAR } from "./palette"

/**
 * Horizontal ranked bars for a single measure — "top N by count".
 *
 * Form choice: magnitude across many named categories with long labels. A
 * horizontal bar keeps the labels readable at their natural length; a column
 * chart would rotate them. Sorted descending, so rank is carried by position
 * and colour is free to stay one hue.
 *
 * Sequential, ONE hue — colour encodes the same measure as length, so it must
 * not become a rainbow. Flagged rows use the reserved status hue and carry a
 * text badge alongside, never colour alone.
 */

export interface RankedBarDatum {
    id: string
    label: string
    value: number
    /** Secondary figure shown after the value, e.g. hours. */
    secondary?: string
    /** Draws the row in the reserved critical hue and shows `flagLabel`. */
    flagged?: boolean
    flagLabel?: string
}

interface RankedBarChartProps {
    data: RankedBarDatum[]
    /** Formats the primary value for display. */
    format?: (value: number) => string
    max?: number
}

export default function RankedBarChart({
    data,
    format = (value) => String(value),
    max = 10,
}: RankedBarChartProps) {
    const [hover, setHover] = useState<string | null>(null)
    const shown = data.slice(0, max)
    if (shown.length === 0) return null

    const peak = Math.max(...shown.map((datum) => datum.value)) || 1

    return (
        <div className="space-y-1">
            {shown.map((datum) => {
                const isHovered = hover === datum.id
                const width = (datum.value / peak) * 100
                return (
                    <div
                        key={datum.id}
                        className={cn(
                            "rounded-md px-2 py-1 transition-colors",
                            isHovered && "bg-muted/60",
                        )}
                        onMouseEnter={() => setHover(datum.id)}
                        onMouseLeave={() => setHover(null)}
                    >
                        <div className="flex items-baseline justify-between gap-3 text-sm">
                            <span className="flex min-w-0 items-center gap-2">
                                <span className="truncate" title={datum.label}>
                                    {datum.label}
                                </span>
                                {datum.flagged && datum.flagLabel && (
                                    <span
                                        className="shrink-0 rounded border px-1 py-px text-[10px] font-medium"
                                        style={{
                                            color: STATUS_VAR.critical,
                                            borderColor: STATUS_VAR.critical,
                                        }}
                                    >
                                        {datum.flagLabel}
                                    </span>
                                )}
                            </span>
                            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                                {format(datum.value)}
                                {datum.secondary && ` · ${datum.secondary}`}
                            </span>
                        </div>
                        <div className="relative mt-1 h-1.5">
                            <div
                                className="absolute inset-0 rounded-full"
                                style={{ background: STATUS_VAR.track }}
                            />
                            <div
                                className="absolute inset-y-0 left-0 transition-all"
                                style={{
                                    width: `${width}%`,
                                    background: datum.flagged ? STATUS_VAR.critical : SEQUENTIAL_VAR,
                                    borderRadius: MARK_RADIUS,
                                }}
                            />
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
